import {
  type EtatLigneRefacturation,
  type EtatLotRefacturation,
  formaterJour,
  formaterMontant,
  ligneRefacturationCreation,
  regrouperLignes,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { api, corps, ErreurApi, type LigneRefacturation, lire, requetes } from '../api'
import {
  LIBELLES_ETAT_LIGNE_REFACTURATION,
  LIBELLES_ETAT_LOT_REFACTURATION,
  TON_ETAT_LIGNE_REFACTURATION,
  TON_ETAT_LOT_REFACTURATION,
} from '../libelles'
import { Formulaire } from './Formulaire'
import { Icone } from './Icone'
import { Section } from './Section'
import { confirmer } from './confirmation'
import { LogoConnu } from './Logo'

// Frais à refacturer : les lignes encore à refacturer, regroupées par couple (qui a réglé, qui
// rembourse) avec leur total, et le lot qui les réunit d'un coup ; les lots préparés et leur
// remboursement ; les lignes d'une pièce. Préparer un lot fait le PDF, l'encaissement attendu,
// la tâche et le courriel (N1) : un humain transmet ; le zip de ses justificatifs (un fichier par
// dépense) se télécharge du lot.

/**
 * Ce qu'un lot donne à transmettre : le zip des justificatifs (un fichier par dépense, pour un
 * outil de notes de frais), le PDF du lot, et sa tâche quand on n'y est pas déjà.
 */
export function LiensLot({
  lotId,
  pieceId,
  tacheId,
  zip = true,
}: {
  lotId: string
  pieceId: string | null
  tacheId?: string | null
  /** Faux pour un lot annulé : ses lignes l'ont quitté. */
  zip?: boolean
}) {
  return (
    <span className="liens-lot">
      {zip && (
        <a
          className="bouton-discret petit"
          href={`/api/lots-refacturation/${lotId}/justificatifs`}
          download
        >
          <Icone nom="folder_zip" taille={16} />
          Justificatifs (zip)
        </a>
      )}
      {pieceId && (
        <Link className="bouton-discret petit" to="/pieces/$id" params={{ id: pieceId }}>
          <Icone nom="picture_as_pdf" taille={16} />
          PDF du lot
        </Link>
      )}
      {tacheId && (
        <Link className="bouton-discret petit" to="/taches/$id" params={{ id: tacheId }}>
          <Icone nom="task_alt" taille={16} />
          Tâche
        </Link>
      )}
    </span>
  )
}

function messageErreur(e: unknown) {
  return e instanceof ErreurApi ? e.message : 'Action impossible.'
}

export function BadgeLigneRefacturation({ etat }: { etat: EtatLigneRefacturation }) {
  return (
    <span className={`badge ${TON_ETAT_LIGNE_REFACTURATION[etat]}`}>
      {LIBELLES_ETAT_LIGNE_REFACTURATION[etat]}
    </span>
  )
}

function BadgeLot({ etat }: { etat: EtatLotRefacturation }) {
  return (
    <span className={`badge ${TON_ETAT_LOT_REFACTURATION[etat]}`}>
      {LIBELLES_ETAT_LOT_REFACTURATION[etat]}
    </span>
  )
}

/** Ce que la ligne dit de sa dépense : date, fournisseur, objet. */
function decrireDepense(l: LigneRefacturation) {
  return [l.date ? formaterJour(l.date) : null, l.piece?.fournisseur, l.libelle]
    .filter(Boolean)
    .join(' · ')
}

/** Motif, origine, justificatif manquant : en une ligne discrète. */
function precisions(l: LigneRefacturation) {
  return [
    l.motif,
    l.origine === 'dossier' && 'posée par la règle du dossier',
    l.origine === 'avance' && 'avance (facture dans la compta de la société)',
    !l.pieceId && 'justificatif à joindre',
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * Lignes à refacturer (d'une entité, sinon de toutes), une carte par couple : chacune se décoche,
 * « Préparer le lot » réunit celles qui restent cochées.
 */
export function ARefacturer({ entiteId }: { entiteId?: string }) {
  const client = useQueryClient()
  const lignes = useQuery(requetes.refacturations(entiteId ? { entiteId } : {}))
  const [decoches, setDecoches] = useState<Set<string>>(new Set())
  const [message, setMessage] = useState<{
    texte: string
    tacheId: string
    lotId: string
    pieceId: string
  } | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const haut = useRef<HTMLDivElement>(null)
  const groupes = regrouperLignes((lignes.data ?? []).filter((l) => l.etat === 'a_refacturer'))

  // La carte du lot préparé disparaît : le message et ses liens restent sous les yeux.
  useEffect(() => {
    if (message) haut.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [message])

  async function agir(fn: () => Promise<unknown>) {
    setErreur(null)
    try {
      await fn()
      await client.invalidateQueries()
    } catch (e) {
      setErreur(messageErreur(e))
    }
  }

  async function preparer(ids: string[]) {
    await agir(async () => {
      const r = await lire(api['lots-refacturation'].$post({ json: { ligneIds: ids } }))
      setDecoches(new Set())
      setMessage({
        texte: `Lot préparé : ${formaterMontant(r.totalCts)} à rembourser. ${r.brouillonId ? 'Le courriel attend dans la boîte d’envoi ; ' : ''}le PDF à transmettre est dans les pièces.${r.manquantes.length > 0 ? ` Justificatifs absents : ${r.manquantes.join(', ')}.` : ''}`,
        tacheId: r.tacheId,
        lotId: r.lotId,
        pieceId: r.pieceId,
      })
    })
  }

  return (
    <div className="pile" style={{ gap: 12 }}>
      {message && (
        <div ref={haut} className="pile" style={{ gap: 6 }}>
          <p className="discret">{message.texte}</p>
          <LiensLot lotId={message.lotId} pieceId={message.pieceId} tacheId={message.tacheId} />
        </div>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
      {lignes.isPending && <p className="discret">Chargement…</p>}
      {lignes.data && groupes.length === 0 && (
        <p className="discret">Rien à refacturer pour l’instant.</p>
      )}
      {groupes.map((g) => {
        const cochees = g.lignes.filter((l) => !decoches.has(l.id))
        const total = cochees.reduce((n, l) => n + l.montantCts, 0)
        return (
          <section key={g.cle} className="carte carte-flush">
            <div className="barre-selection">
              <Icone nom="receipt_long" taille={19} />
              <strong>
                {g.lignes[0]!.entite} → {g.lignes[0]!.destinataire}
              </strong>
              <span className="discret">
                {g.lignes.length} pièce{g.lignes.length > 1 ? 's' : ''},{' '}
                {formaterMontant(g.totalCts, g.devise)}
              </span>
              <button
                type="button"
                className="bouton"
                disabled={cochees.length === 0}
                onClick={() => preparer(cochees.map((l) => l.id))}
              >
                <Icone nom="forward_to_inbox" taille={17} />
                Préparer le lot ({formaterMontant(total, g.devise)})
              </button>
            </div>
            {g.lignes.map((l) => (
              <div key={l.id} className="ligne">
                <span className="case" style={{ width: 20 }}>
                  <input
                    type="checkbox"
                    aria-label="Mettre dans le lot"
                    checked={!decoches.has(l.id)}
                    onChange={(e) =>
                      setDecoches((d) => {
                        const n = new Set(d)
                        if (e.target.checked) n.delete(l.id)
                        else n.add(l.id)
                        return n
                      })
                    }
                  />
                </span>
                {l.pieceId ? (
                  <Link to="/pieces/$id" params={{ id: l.pieceId }} className="ligne-contenu">
                    <span className="ligne-titre">
                      <span className="titre-logo">
                        <LogoConnu nom={l.piece?.fournisseur ?? l.libelle} taille={24} />
                        <strong>{decrireDepense(l) || 'Pièce'}</strong>
                      </span>
                    </span>
                    <span className="discret">{precisions(l)}</span>
                  </Link>
                ) : (
                  <div className="ligne-contenu">
                    <span className="ligne-titre">
                      <span className="titre-logo">
                        <LogoConnu nom={l.piece?.fournisseur ?? l.libelle} taille={24} />
                        <strong>{decrireDepense(l) || 'Dépense'}</strong>
                      </span>
                      <span className="badge ton-ambre">Justificatif à joindre</span>
                    </span>
                    <span className="discret">{precisions(l)}</span>
                  </div>
                )}
                <div className="montant">
                  <span>{formaterMontant(l.montantCts, l.devise)}</span>
                  <button
                    type="button"
                    className="bouton-discret"
                    onClick={() =>
                      agir(() =>
                        lire(
                          api.refacturations[':id'].action.$post({
                            param: { id: l.id },
                            json: { action: 'abandonner' },
                          }),
                        ),
                      )
                    }
                  >
                    Abandonner
                  </button>
                </div>
              </div>
            ))}
          </section>
        )
      })}
    </div>
  )
}

/** Lots préparés : remboursement attendu, remboursé ou annulé ; PDF et tâche à portée. */
export function LotsRefacturation({ entiteId }: { entiteId?: string }) {
  const client = useQueryClient()
  const lots = useQuery(requetes.lotsRefacturation(entiteId))
  const [erreur, setErreur] = useState<string | null>(null)
  if (!lots.data || lots.data.length === 0) return null
  return (
    <Section titre="Lots préparés">
      {erreur && <p className="alerte">{erreur}</p>}
      {lots.data.map((lot) => (
        <div key={lot.id} className="ligne">
          <div className="ligne-contenu">
            <span className="ligne-titre">
              <strong>
                {lot.entite} → {lot.destinataire}
              </strong>
              <BadgeLot etat={lot.etat} />
            </span>
            <span className="discret">
              {formaterJour(String(lot.creeLe).slice(0, 10))} · {lot.nombreLignes} pièce
              {lot.nombreLignes > 1 ? 's' : ''}
            </span>
            <LiensLot
              lotId={lot.id}
              pieceId={lot.pieceId}
              tacheId={lot.tacheId}
              zip={lot.nombreLignes > 0}
            />
          </div>
          <div className="montant">
            <span>{formaterMontant(lot.totalCts, lot.devise)}</span>
            {lot.etat === 'attendu' && (
              <button
                type="button"
                className="bouton-discret"
                onClick={async () => {
                  if (!(await confirmer('Annuler ce lot ? Ses pièces redeviennent à refacturer.')))
                    return
                  setErreur(null)
                  try {
                    await lire(
                      api['lots-refacturation'][':id'].annuler.$post({ param: { id: lot.id } }),
                    )
                    await client.invalidateQueries()
                  } catch (e) {
                    setErreur(messageErreur(e))
                  }
                }}
              >
                Annuler le lot
              </button>
            )}
          </div>
        </div>
      ))}
    </Section>
  )
}

/** Sur la fiche d'une pièce : à qui elle se refacture, et l'ajout d'une ligne. */
export function RefacturationPiece({ pieceId, entiteId }: { pieceId: string; entiteId: string }) {
  const client = useQueryClient()
  const lignes = useQuery(requetes.refacturations({ pieceId }))
  const entites = useQuery(requetes.entites).data ?? []
  const tiers = useQuery(requetes.tiers).data ?? []
  const [erreur, setErreur] = useState<string | null>(null)
  const communes = entites.filter((e) => !e.archivee && !e.privee)
  const options = [
    ...communes.map((e) => ({ valeur: `e:${e.id}`, libelle: e.nom })),
    ...tiers.map((t) => ({ valeur: `t:${t.id}`, libelle: t.nom })),
  ]
  const payeurs = communes.map((e) => ({ valeur: e.id, libelle: e.nom }))
  async function agir(id: string, action: 'abandonner' | 'rouvrir') {
    setErreur(null)
    try {
      await lire(api.refacturations[':id'].action.$post({ param: { id }, json: { action } }))
      await client.invalidateQueries({ queryKey: ['refacturations'] })
    } catch (e) {
      setErreur(messageErreur(e))
    }
  }
  return (
    <Section
      titre="À refacturer"
      libelleAjout="Refacturer"
      ajout={(fermer) => (
        <Formulaire
          initial={{ entiteId }}
          champs={[
            {
              nom: 'entiteId',
              libelle: 'Réglée par',
              type: 'choix',
              requis: true,
              options: payeurs,
              aide: 'Le foyer pour une avance : la facture reste dans la compta de cette entité.',
            },
            {
              nom: 'destinataire',
              libelle: 'À rembourser par',
              type: 'choix',
              requis: true,
              options,
            },
            {
              nom: 'montantCts',
              libelle: 'Montant (€)',
              type: 'montant',
              aide: 'Vide : tout ce qui reste de la pièce.',
            },
            { nom: 'motif', libelle: 'Motif', type: 'texte' },
          ]}
          libelleBouton="Mettre à refacturer"
          onAnnuler={fermer}
          onValider={async (v) => {
            const [sorte, id] = String(v.destinataire).split(':')
            await lire(
              api.refacturations.$post({
                json: corps(ligneRefacturationCreation, {
                  pieceId,
                  entiteId: v.entiteId,
                  ...(sorte === 'e' ? { destinataireEntiteId: id } : { destinataireTiersId: id }),
                  ...(v.montantCts !== null && { montantCts: v.montantCts }),
                  motif: v.motif,
                }),
              }),
            )
            await client.invalidateQueries({ queryKey: ['refacturations'] })
            fermer()
          }}
        />
      )}
    >
      {erreur && <p className="alerte">{erreur}</p>}
      {(lignes.data ?? []).length === 0 && (
        <p className="discret">Réglée par l’entité, sans rien à se faire rembourser.</p>
      )}
      {(lignes.data ?? []).map((l) => (
        <div key={l.id} className="ligne">
          <div className="ligne-contenu">
            <span className="ligne-titre">
              <strong>
                {l.entite} → {l.destinataire}
              </strong>
              <BadgeLigneRefacturation etat={l.etat} />
            </span>
            <span className="discret">
              {precisions(l) || (
                <Link to="/refacturations" search={{ entiteId: l.entiteId }}>
                  Voir les frais à refacturer
                </Link>
              )}
            </span>
          </div>
          <div className="montant">
            <span>{formaterMontant(l.montantCts, l.devise)}</span>
            {(l.etat === 'a_refacturer' || l.etat === 'abandonnee') && (
              <button
                type="button"
                className="bouton-discret"
                onClick={() => agir(l.id, l.etat === 'abandonnee' ? 'rouvrir' : 'abandonner')}
              >
                {l.etat === 'abandonnee' ? 'Rouvrir' : 'Abandonner'}
              </button>
            )}
          </div>
        </div>
      ))}
    </Section>
  )
}

/** Une dépense sans pièce (indemnités kilométriques, facture pas encore reçue). */
export function DepenseSansPiece({ entiteId }: { entiteId?: string | undefined }) {
  const client = useQueryClient()
  const entites = useQuery(requetes.entites).data ?? []
  const tiers = useQuery(requetes.tiers).data ?? []
  const communes = entites.filter((e) => !e.archivee && !e.privee)
  return (
    <Section
      titre="Dépense sans pièce"
      libelleAjout="Noter une dépense"
      ajout={(fermer) => (
        <Formulaire
          initial={{ entiteId: entiteId ?? communes.find((e) => e.type === 'foyer')?.id ?? null }}
          champs={[
            {
              nom: 'entiteId',
              libelle: 'Réglée par',
              type: 'choix',
              requis: true,
              options: communes.map((e) => ({ valeur: e.id, libelle: e.nom })),
            },
            {
              nom: 'destinataire',
              libelle: 'À rembourser par',
              type: 'choix',
              requis: true,
              options: [
                ...communes.map((e) => ({ valeur: `e:${e.id}`, libelle: e.nom })),
                ...tiers.map((t) => ({ valeur: `t:${t.id}`, libelle: t.nom })),
              ],
            },
            { nom: 'libelle', libelle: 'Dépense', type: 'texte', requis: true },
            { nom: 'date', libelle: 'Date', type: 'date', requis: true },
            { nom: 'montantCts', libelle: 'Montant (€)', type: 'montant', requis: true },
            { nom: 'motif', libelle: 'Motif', type: 'texte' },
          ]}
          libelleBouton="Mettre à refacturer"
          onAnnuler={fermer}
          onValider={async (v) => {
            const [sorte, id] = String(v.destinataire).split(':')
            await lire(
              api.refacturations.$post({
                json: corps(ligneRefacturationCreation, {
                  entiteId: v.entiteId,
                  ...(sorte === 'e' ? { destinataireEntiteId: id } : { destinataireTiersId: id }),
                  libelle: v.libelle,
                  date: v.date,
                  montantCts: v.montantCts,
                  motif: v.motif,
                }),
              }),
            )
            await client.invalidateQueries({ queryKey: ['refacturations'] })
            fermer()
          }}
        />
      )}
    >
      <p className="discret">
        Indemnités kilométriques, facture pas encore reçue : la ligne attend son justificatif, à
        joindre quand la pièce arrive.
      </p>
    </Section>
  )
}
